import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { styles } from '@actual-app/components/styles';
import { css, keyframes } from '@emotion/css';

import { colorGavi } from '#components/mobile/travesia/gaviCss';
import { color } from '#components/mobile/ui/tokens';

/** Latón del ojo de buey, la flecha y los nudos (ilustración: igual en los dos temas). */
export const LATON = {
  claro: '#f2d38a',
  medio: '#c9973b',
  oscuro: '#8a6420',
} as const;

const asoma = keyframes({
  '0%, 55%, 100%': { transform: 'translateY(13px)' },
  '64%, 88%': { transform: 'translateY(0)' },
  '72%': { transform: 'translateY(0) rotate(-8deg)' },
  '80%': { transform: 'translateY(0) rotate(6deg)' },
});
const interroga = keyframes({
  '0%, 55%, 100%': { opacity: 1, transform: 'none' },
  '62%, 90%': { opacity: 0, transform: 'translateY(-4px)' },
});

const claseOjo = css({
  '& .asoma': {
    transformOrigin: '23px 40px',
    transform: 'translateY(13px)',
    animation: `${asoma} 5s ease-in-out infinite`,
  },
  '& .interrogante': {
    animation: `${interroga} 5s ease-in-out infinite`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& .asoma, & .interrogante': { animation: 'none' },
  },
});

/**
 * Botón de ayuda de Rumbo: un ojo de buey de latón (la ventanita redonda de
 * los barcos) con Gavi asomando de vez en cuando junto a un «?». Abre la
 * ayuda de la pantalla.
 */
export function OjoBuey({ onPress }: { onPress: () => void }) {
  const { t } = useTranslation();
  // Ids propios: puede haber más de un ojo de buey en el documento.
  const id = useId().replace(/:/g, '');
  return (
    <Button
      variant="bare"
      data-testid="ojo-de-buey"
      aria-label={t('Help: Gavi explains this screen')}
      onPress={onPress}
      className={claseOjo}
      style={{
        ...styles.noTapHighlight,
        width: 46,
        height: 46,
        minWidth: 46,
        padding: 0,
        borderRadius: '50%',
        flexShrink: 0,
      }}
    >
      <svg viewBox="0 0 46 46" width={46} height={46} aria-hidden>
        <defs>
          <radialGradient id={`${id}c`} cx="40%" cy="35%" r="70%">
            <stop offset="0" stopColor="#cfe6f5" />
            <stop offset=".7" stopColor="#7fb1d3" />
            <stop offset="1" stopColor="#4f86ad" />
          </radialGradient>
          <linearGradient id={`${id}l`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={LATON.claro} />
            <stop offset=".5" stopColor={LATON.medio} />
            <stop offset="1" stopColor={LATON.oscuro} />
          </linearGradient>
          <clipPath id={`${id}v`}>
            <circle cx="23" cy="23" r="15" />
          </clipPath>
        </defs>
        <circle cx="23" cy="23" r="21.5" fill={`url(#${id}l)`} />
        <circle cx="23" cy="23" r="16.5" fill={LATON.oscuro} />
        <circle cx="23" cy="23" r="15" fill={`url(#${id}c)`} />
        <g clipPath={`url(#${id}v)`}>
          <path
            d="M6 33 q5 -3 10 0 t10 0 t10 0 t10 0 V46 H6 Z"
            fill="#2f6f9a"
            opacity=".55"
          />
          <g className="asoma">
            <circle
              cx="23"
              cy="29"
              r="9"
              fill={colorGavi.blanco}
              stroke="rgba(14,20,36,.25)"
            />
            <circle cx="26" cy="27" r="1.9" fill={colorGavi.ojo} />
            <circle cx="26.6" cy="26.4" r=".6" fill={colorGavi.brillo} />
            <path d="M31 30 L38 32 L31 34 Z" fill={colorGavi.pico} />
          </g>
        </g>
        <g className="interrogante">
          <circle
            cx="35"
            cy="10"
            r="8"
            fill={color.accent}
            stroke="#fff"
            strokeWidth="1.6"
          />
          <text
            x="35"
            y="14"
            textAnchor="middle"
            fontWeight="800"
            fontSize="11"
            fill="#fff"
          >
            ?
          </text>
        </g>
        {[
          [23, 2.6],
          [23, 43.4],
          [2.6, 23],
          [43.4, 23],
        ].map(([cx, cy]) => (
          <circle
            key={`${cx}-${cy}`}
            cx={cx}
            cy={cy}
            r="1.2"
            fill={LATON.oscuro}
          />
        ))}
      </svg>
    </Button>
  );
}
